import { Link } from "react-router-dom";
import { Star, MapPin } from "lucide-react";
import { Card, Badge } from "./ui/Card";
import { formatPrice } from "../lib/utils";

/**
 * A shop as a row in a list of results. `searchTerm` is the search that
 * listed it, passed along so a booking can be credited to that search.
 * `labels` replaces the card's own words on a page in another language.
 */
export default function ListingCard({ business, searchTerm, labels = {} }) {
  const photo = business.venue_photo_urls?.[0] || business.logo_url;
  const location = business.city || business.address;

  return (
    <Link
      to={`/${business.slug}`}
      state={{ source: "marketplace", ...(searchTerm && { searchTerm }) }}
      className="group block"
    >
      <Card className="flex flex-col overflow-hidden transition-shadow hover:shadow-md sm:flex-row">
        <div className="relative h-44 w-full shrink-0 overflow-hidden bg-muted sm:h-auto sm:w-56">
          {photo ? (
            <img
              src={photo}
              alt={business.name}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-4xl font-montserrat font-bold text-primary/30">
              {business.name?.[0]}
            </div>
          )}
          {business.is_promoted && (
            <Badge variant="dark" className="absolute left-2 top-2">
              {labels.promoted || "Promoted"}
            </Badge>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-2 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="truncate font-montserrat text-base font-semibold text-slate-900">
                {business.name}
              </h3>
              <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                {location}
                {business.distance_km != null && ` · ${business.distance_km} km`}
              </p>
            </div>

            {business.rating_count > 0 ? (
              <div className="flex shrink-0 items-center gap-1 rounded-full bg-slate-900/90 px-2.5 py-1 text-xs font-semibold text-white">
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                {Number(business.rating_avg).toFixed(1)}
                <span className="text-white/70">({business.rating_count})</span>
              </div>
            ) : (
              <Badge variant="accent" className="shrink-0">
                {labels.new || "New"}
              </Badge>
            )}
          </div>

          {business.description && (
            <p className="line-clamp-2 text-sm text-muted-foreground">{business.description}</p>
          )}

          {business.services?.length > 0 && (
            <ul className="space-y-1 text-sm text-slate-700">
              {business.services.map((service) => (
                <li key={service.id} className="flex items-baseline justify-between gap-3">
                  <span className="truncate">{service.name}</span>
                  <span className="shrink-0 text-muted-foreground">
                    {formatPrice(service.price)} · {service.duration_minutes} min
                  </span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1">
            {business.category?.name && <Badge variant="default">{business.category.name}</Badge>}
            <span className="ml-auto text-sm font-semibold text-primary group-hover:underline">
              {labels.see_details || "See details"}
            </span>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export function ListingCardSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm sm:flex-row">
      <div className="h-44 w-full animate-pulse bg-muted sm:h-40 sm:w-56" />
      <div className="flex-1 space-y-2 p-4">
        <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
        <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
        <div className="h-3 w-full animate-pulse rounded bg-muted" />
        <div className="h-6 w-24 animate-pulse rounded-full bg-muted" />
      </div>
    </div>
  );
}
